import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import { Permissions } from '../../constants/permissions';
import { driversApi, vehiclesApi, DriverDto, CreateDriverDto, UpdateDriverDto, VehicleDto, UserDto, usersApi } from '../../services/api';
import { usePermission } from '../../hooks/usePermission';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';

export const DriversPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const canView = usePermission(Permissions.Logistics.ViewDrivers);
  const canCreate = usePermission(Permissions.Logistics.CreateDrivers);
  const canEdit = usePermission(Permissions.Logistics.EditDrivers);
  const canDelete = usePermission(Permissions.Logistics.DeleteDrivers);
  
  const [drivers, setDrivers] = useState<DriverDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [vehicles, setVehicles] = useState<VehicleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState<DriverDto | null>(null);
  const [formData, setFormData] = useState<CreateDriverDto & { isActive?: boolean }>({
    userId: '',
    licenseNumber: '',
    licenseExpiryDate: undefined,
    phone: '',
    defaultVehicleId: undefined,
  });

  useEffect(() => {
    setPageConfig({
      title: 'Conductores',
      actions: canCreate ? [
        {
          label: 'Nuevo Conductor',
          icon: Plus,
          onClick: () => {
            setEditingDriver(null);
            setFormData({ userId: '', licenseNumber: '', licenseExpiryDate: undefined, phone: '', defaultVehicleId: undefined });
            setShowForm(true);
          },
          variant: 'primary' as const
        }
      ] : []
    });
  }, [setPageConfig, canCreate]);

  const loadData = async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const [driversData, usersData, vehiclesData] = await Promise.all([
        driversApi.getAll({ isActive: showInactive ? undefined : true }),
        usersApi.getAll(),
        vehiclesApi.getAll({ isActive: true })
      ]);
      setDrivers(driversData);
      setUsers(usersData);
      setVehicles(vehiclesData);
    } catch (error) {
      alert('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [showInactive]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingDriver) {
        await driversApi.update(editingDriver.id, formData as UpdateDriverDto);
        alert('Conductor actualizado');
      } else {
        await driversApi.create(formData);
        alert('Conductor creado');
      }
      setShowForm(false);
      loadData();
    } catch (error: any) {
      alert(error.message || 'Error al guardar conductor');
    }
  };

  const handleEdit = (driver: DriverDto) => {
    setEditingDriver(driver);
    setFormData({
      userId: driver.userId,
      licenseNumber: driver.licenseNumber,
      licenseExpiryDate: driver.licenseExpiryDate || undefined,
      phone: driver.phone || '',
      defaultVehicleId: driver.defaultVehicleId || undefined,
      isActive: driver.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este conductor?')) return;
    try {
      await driversApi.delete(id);
      alert('Conductor eliminado');
      loadData();
    } catch (error: any) {
      alert(error.message || 'Error al eliminar');
    }
  };

  const filteredDrivers = drivers.filter(d => 
    d.userName.toLowerCase().includes(search.toLowerCase()) ||
    d.licenseNumber.toLowerCase().includes(search.toLowerCase())
  );

  if (!canView) return <div className="p-6">No tienes permisos para ver conductores</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o licencia..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="show-inactive"
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4"
          />
          <Label htmlFor="show-inactive">Mostrar inactivos</Label>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredDrivers.map((driver) => (
            <Card key={driver.id} className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold">{driver.userName}</h3>
                  <p className="text-sm text-muted-foreground">Licencia: {driver.licenseNumber}</p>
                  {driver.phone && <p className="text-sm">Tel: {driver.phone}</p>}
                  {driver.licenseExpiryDate && (
                    <p className="text-sm">Vence: {new Date(driver.licenseExpiryDate).toLocaleDateString()}</p>
                  )}
                  {driver.vehicleName && <p className="text-sm">Vehículo: {driver.vehicleName}</p>}
                </div>
                <span className={`px-2 py-1 text-xs rounded ${driver.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {driver.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="flex gap-2 justify-end">
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => handleEdit(driver)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {canDelete && (
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(driver.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editingDriver ? 'Editar Conductor' : 'Nuevo Conductor'}</DialogTitle>
              <DialogDescription>
                Complete los datos del conductor
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="userId">Usuario *</Label>
                <select
                  id="userId"
                  value={formData.userId}
                  onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                  disabled={!!editingDriver}
                  required
                  className="w-full bg-white border border-gray-300 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5"
                >
                  <option value="">Seleccionar usuario</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.fullName || u.username || u.email || 'Sin nombre'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="licenseNumber">Número de licencia *</Label>
                <Input
                  id="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="licenseExpiryDate">Fecha de vencimiento</Label>
                <Input
                  id="licenseExpiryDate"
                  type="date"
                  value={formData.licenseExpiryDate || ''}
                  onChange={(e) => setFormData({ ...formData, licenseExpiryDate: e.target.value || undefined })}
                />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="defaultVehicleId">Vehículo por defecto</Label>
                <select
                  id="defaultVehicleId"
                  value={formData.defaultVehicleId || ''}
                  onChange={(e) => setFormData({ ...formData, defaultVehicleId: e.target.value || undefined })}
                  className="w-full bg-white border border-gray-300 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5"
                >
                  <option value="">Ninguno</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.licensePlate})
                    </option>
                  ))}
                </select>
              </div>
              {editingDriver && (
                <div className="flex items-center gap-2">
                  <input
                    id="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="isActive">Activo</Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
